import { css, cx } from "@emotion/css"
import { useQuery } from "react-query"
import { http } from "api/http"
import { DataEnvelop } from "@dashboards/helpers/api"

import { Loading } from "@dashboards/components/Loading"
import { Empty } from "@dashboards/components/Empty"
import { Card, CardTitle } from "@dashboards/components/Card"
import { StackBars } from "@dashboards/components/html/StackBars"
import { makeGroups, emptyGroups } from "@dashboards/components/lib/groups"
import {
  getMediaColorByIndex,
  getMediaColorByKey,
} from "@dashboards/helpers/colors"
import { currencyLong } from "@dashboards/helpers/formatters"
import useFilters from "../../filters/hook"
import { Legends } from "@dashboards/components/Legends"

const styles = {
  card: css`
    min-height: 300px;
  `,
  container: css`
    flex: 1;
    padding: var(--spacing-l, 20px) 0;
  `,
  legends: css`
    padding: var(--spacing-xs, 24px) 0;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  `,
}

export default function TopSuppliers() {
  const { getSearchParams } = useFilters()
  const { data, status } = useQuery(
    ["reporting/regional/top-suppliers", getSearchParams()],
    fetchData,
  )
  const isEmpty =
    status === "success" && (data === undefined || data?.max === 0)

  const sp = new URLSearchParams(getSearchParams())
  const currency = sp.get("currency") || undefined

  return (
    <Card className={cx(styles.card)}>
      <CardTitle>Top Suppliers</CardTitle>
      {status === "loading" ? (
        <Loading />
      ) : isEmpty ? (
        <Empty />
      ) : status === "error" ? (
        <Empty title="something went wrong" />
      ) : (
        <div className={styles.container}>
          <Legends
            keys={data?.keys ?? []}
            getColor={getMediaColorByKey}
            direction="row"
            className={styles.legends}
          />
          <StackBars
            groups={data ?? emptyGroups}
            formatValue={currencyLong(currency)}
            getColor={getMediaColorByIndex}
            className={css`
              .name {
                max-width: 180px;
              }
            `}
          />
        </div>
      )}
    </Card>
  )
}

type ResponseItem = {
  supplierName: string
  totalSpend: number
  marketName: string
  mediaTypeName: string
}

type Response = DataEnvelop<ResponseItem>

function makeData(res: Response) {
  const data = res?.data ?? []
  const groups = makeGroups(
    data,
    (x) => x.supplierName,
    (x) => x.mediaTypeName,
    (x) => x.totalSpend,
  )
  return groups
}

const fetchData = async (key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res: Response = await http(key, { searchParams, timeout: false }).json()
  return makeData(res)
}
